// Pagina404.js — a rota `path="*"` do App.js.
//
// Até agora o site não tinha esta tela: qualquer endereço fora das rotas
// declaradas renderizava cabeçalho e rodapé com o miolo VAZIO. Quem caía num
// link antigo via uma página que parecia meio carregada, sem erro e sem saída
// — a pior das duas leituras possíveis, porque não dá nem o diagnóstico nem
// o próximo passo.
//
// Duas decisões de tom, para não serem desfeitas por engano:
//
//  1. Nada de "Oops!", desenho triste ou pedido de desculpas. Endereço errado
//     é rotina; dramatizar transforma um contratempo de dois segundos em um
//     evento. O texto diz o que houve, mostra o endereço que a pessoa abriu
//     (quase sempre o suficiente para ela mesma ver o erro de digitação) e
//     oferece três saídas de verdade.
//  2. A busca leva para /livros?busca=, o MESMO parâmetro que o cabeçalho
//     escreve e que o GridProduto lê — não é um campo decorativo. Quem chegou
//     aqui por um link quebrado de livro digita o título e continua a compra
//     sem passar pela home.
//
// Sobre o realce do campo: `.campo-foco` (anel nítido só no foco). O halo
// desfocado que existiu na busca do FAQ foi vetado — sobre navy o laranja
// borrado vira mancha marrom. O App.css registra isso junto da classe.
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Reveal from "../ui/Reveal";
import CaixaVazia from "../ui/CaixaVazia";
import { Ornamento } from "../ui/TituloDeSecao";

const Pagina404 = () => {
  const main = process.env.REACT_APP_MAIN || "/";
  const location = useLocation();
  const navigate = useNavigate();
  const [termo, setTermo] = useState("");

  const buscar = (e) => {
    e.preventDefault();
    const t = termo.trim();
    // Sem termo o parâmetro não entra na URL: `?busca=` vazio faria o
    // GridProduto montar o chip 'Busca: ""' e sugerir um filtro que não existe.
    navigate(t ? `${main}livros?busca=${encodeURIComponent(t)}` : `${main}livros`);
  };

  return (
    <section className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] pb-16 px-6 text-white">
      <Reveal className="max-w-[640px] mx-auto flex flex-col items-center text-center">
        {/* A cor do contorno da caixa vem de `currentColor` — por isso a classe
            de texto está aqui, e não numa prop nova do componente. */}
        <CaixaVazia className="text-insigne-orange" largura={200} />

        <span className="chapeu chapeu-centro mt-[1.5rem]">Erro 404</span>

        <h1 className="titulo-display mb-0 mt-[0.75rem] text-[2.8rem] sm:text-[3.6rem]">
          Não encontramos esta <span className="text-insigne-orange">página</span>
        </h1>
        <Ornamento className="mt-[1rem]" />

        <p className="mt-3.5 text-sm sm:text-base text-white/60 max-w-[52ch]">
          O endereço que você abriu não corresponde a nenhuma página do site. Costuma
          ser um link antigo, um caractere a mais no endereço ou um livro que saiu
          do catálogo.
        </p>

        {/* O endereço tentado resolve boa parte dos casos sozinho: a pessoa lê,
            vê o erro de digitação e corrige. `break-all` porque URL colada de
            e-mail vem longa e sem espaço onde quebrar. */}
        <code className="mt-6 max-w-full break-all rounded-full border border-line bg-insigne-navy-soft px-6 py-1.5 text-xs text-white/45">
          {location.pathname}
        </code>

        {/* ===== Saída 1: procurar o que a pessoa veio buscar ===== */}
        <form onSubmit={buscar} role="search" className="w-full max-w-[420px] mt-8">
          <label htmlFor="busca-404" className="sr-only">
            Buscar um livro no catálogo
          </label>
          <div className="campo-foco flex items-center rounded-full border border-line bg-insigne-navy-soft px-6 py-2.5 transition-shadow">
            <input
              id="busca-404"
              type="search"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              placeholder="Título ou autor"
              className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder:text-white/40 outline-none"
            />
            <button
              type="submit"
              aria-label="Buscar no catálogo"
              className="shrink-0 pl-3.5 text-white/50 hover:text-insigne-orange transition-colors"
            >
              <i className="bi bi-search text-lg" aria-hidden="true" />
            </button>
          </div>
        </form>
      </Reveal>

      {/* Um segundo Reveal, com atraso curto: as saídas chegam depois do
          diagnóstico. É a ordem em que a página é lida. */}
      <Reveal
        delay={0.12}
        className="max-w-[640px] mx-auto flex flex-col items-center text-center"
      >
        {/* ===== Saída 2: o catálogo inteiro ===== */}
        {/* `.btn-solido` já traz fundo, borda tracejada e a inversão no hover —
            repetir bg-/border- aqui só brigaria com ela. */}
        <Link
          to={main + "livros"}
          className="btn-solido inline-block mt-8 px-7 py-2.5 rounded-full font-semibold uppercase text-sm"
        >
          Voltar ao catálogo
        </Link>

        {/* ===== Saída 3: home e contato, para quem não veio por um livro ===== */}
        <p className="mt-6 text-sm text-white/50">
          Ou volte para a{" "}
          <Link to={main} className="link-sublinhado text-insigne-orange font-semibold">
            página inicial
          </Link>
          . Se o link quebrado era nosso,{" "}
          <Link to={main + "contato"} className="link-sublinhado text-insigne-orange font-semibold">
            avise a gente
          </Link>
          .
        </p>
      </Reveal>
    </section>
  );
};

export default Pagina404;
